/**
 * Organisation structure import (ORG-16, OH-40) — Keka's "Import departments / locations /
 * designations / grades" from a spreadsheet.
 *
 * Two steps, same body: PREVIEW checks every row and says what would happen (create, update,
 * unchanged, or the problems), writing nothing; IMPORT applies the file only when no row has a
 * problem, all in one transaction, so a file is never half-imported. Rows are matched by `code`
 * (case-insensitive): a known code updates that record, a new code creates one. Rows may refer to
 * each other (a department's parent defined further down the same file).
 *
 * Column keys are the lower-case, underscore form of the header ("Parent Code" → parent_code);
 * `ORG_IMPORT_COLUMNS` is what the template download and the API agree on.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, idempotencyKeySchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const ORG_IMPORT_KINDS = ['departments', 'locations', 'designations', 'grades'] as const;
export type OrgImportKind = (typeof ORG_IMPORT_KINDS)[number];

export interface OrgImportColumn {
  key: string;
  label: string;
  required: boolean;
  example: string;
  help: string;
}

export const ORG_IMPORT_COLUMNS: Record<OrgImportKind, readonly OrgImportColumn[]> = {
  departments: [
    { key: 'code', label: 'Code', required: true, example: 'ENG', help: 'Unique, up to 20 letters, digits, - or _' },
    { key: 'name', label: 'Name', required: true, example: 'Engineering', help: '' },
    { key: 'parent_code', label: 'Parent Code', required: false, example: '', help: 'Code of the department it sits under' },
    { key: 'head_employee_code', label: 'Head Employee Code', required: false, example: 'EMP0001', help: 'A current employee' },
    { key: 'status', label: 'Status', required: false, example: 'Active', help: 'Active or Inactive' },
  ],
  locations: [
    { key: 'code', label: 'Code', required: true, example: 'BLR-HQ', help: 'Unique, up to 20 letters, digits, - or _' },
    { key: 'name', label: 'Name', required: true, example: 'Bengaluru head office', help: '' },
    { key: 'legal_entity_code', label: 'Legal Entity Code', required: false, example: 'ACME-IN', help: '' },
    { key: 'country', label: 'Country', required: true, example: 'IN', help: 'Two-letter country code' },
    { key: 'state_code', label: 'State Code', required: false, example: 'KA', help: 'Required for India' },
    { key: 'city', label: 'City', required: false, example: 'Bengaluru', help: '' },
    { key: 'address', label: 'Address', required: false, example: '12 MG Road', help: '' },
    { key: 'postal_code', label: 'Postal Code', required: false, example: '560001', help: '' },
    { key: 'timezone', label: 'Timezone', required: false, example: 'Asia/Kolkata', help: "Defaults to the country's main zone" },
    { key: 'weekly_off', label: 'Weekly Off', required: false, example: 'Sat,Sun', help: 'Days off, comma separated' },
    { key: 'status', label: 'Status', required: false, example: 'Active', help: 'Active or Inactive' },
  ],
  designations: [
    { key: 'code', label: 'Code', required: true, example: 'SWE-SR', help: 'Unique, up to 20 letters, digits, - or _' },
    { key: 'title', label: 'Title', required: true, example: 'Senior Software Engineer', help: '' },
    { key: 'department_code', label: 'Department Code', required: false, example: 'ENG', help: '' },
    { key: 'location_code', label: 'Location Code', required: false, example: '', help: 'Blank for all locations' },
    { key: 'grade_code', label: 'Grade Code', required: false, example: 'L3', help: '' },
    { key: 'status', label: 'Status', required: false, example: 'Active', help: 'Active or Inactive' },
  ],
  grades: [
    { key: 'code', label: 'Code', required: true, example: 'L3', help: 'Unique, up to 20 characters' },
    { key: 'name', label: 'Name', required: true, example: 'Senior', help: '' },
    { key: 'rank', label: 'Rank', required: false, example: '3', help: 'Higher means more senior' },
    { key: 'description', label: 'Description', required: false, example: '', help: '' },
    { key: 'status', label: 'Status', required: false, example: 'Active', help: 'Active or Inactive' },
  ],
};

/** "Parent Code" / "parent code" / "PARENT_CODE" → "parent_code". */
export function orgImportColumnKey(header: string): string {
  return header.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

/** At most this many rows per file: enough for any org structure, small enough for one request. */
export const ORG_IMPORT_MAX_ROWS = 1000;

export const orgImportBodySchema = z.object({
  rows: z
    .array(z.record(z.string().max(60), z.string().max(500)))
    .min(1, 'The file has no rows')
    .max(ORG_IMPORT_MAX_ROWS, `At most ${ORG_IMPORT_MAX_ROWS} rows per file`),
});

export const orgImportRowResultSchema = z.object({
  /** 1-based data row number (the header is row 0), as a spreadsheet shows it minus one. */
  row: z.number().int(),
  code: z.string().nullable(),
  action: z.enum(['create', 'update', 'unchanged', 'error']),
  problems: z.array(z.string()),
});
export type OrgImportRowResult = z.infer<typeof orgImportRowResultSchema>;

export const orgImportResultSchema = z.object({
  kind: z.enum(ORG_IMPORT_KINDS),
  rows: z.array(orgImportRowResultSchema),
  creates: z.number().int(),
  updates: z.number().int(),
  unchanged: z.number().int(),
  errors: z.number().int(),
  /** True for the import call once applied; false for a preview. */
  applied: z.boolean(),
});
export type OrgImportResult = z.infer<typeof orgImportResultSchema>;

const kindParams = z.object({ kind: z.enum(ORG_IMPORT_KINDS) });
/** Each kind needs its own manage permission at org scope; the route admits any of them. */
const importMeta = routeMeta([
  'department:manage',
  'location:manage',
  'post:manage',
  'org.settings:update',
]);

export const orgImportContract = c.router(
  {
    previewOrgImport: {
      method: 'POST',
      path: '/import/:kind/preview',
      pathParams: kindParams,
      body: orgImportBodySchema,
      responses: {
        200: orgImportResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Check an organisation structure file and say what importing it would do (writes nothing)',
      metadata: importMeta,
    },
    runOrgImport: {
      method: 'POST',
      path: '/import/:kind',
      pathParams: kindParams,
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: orgImportBodySchema,
      responses: {
        200: orgImportResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Import departments, locations, designations or grades: all rows or none, refused while any row has a problem',
      metadata: importMeta,
    },
  },
  { pathPrefix: '/organisation' },
);
